import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    output,
    signal,
} from '@angular/core';
import { PhotoCaptureFile } from '@cpw/domain';
import { SharedUiModule } from '@pf/shared-ui';
import { CpwPhotoCaptureComponent } from './cpw-photo-capture.component';

@Component({
    selector: 'cpw-photo-gallery',
    standalone: true,
    imports: [SharedUiModule, CpwPhotoCaptureComponent],
    templateUrl: './cpw-photo-gallery.component.html',
    styleUrl: './cpw-photo-gallery.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwPhotoGalleryComponent {
    readonly initialPhotos = input<PhotoCaptureFile[]>([]);
    readonly maxPhotos = input<number>(3);
    readonly disabled = input<boolean>(false);

    readonly photosChanged = output<PhotoCaptureFile[]>();

    readonly photos = signal<PhotoCaptureFile[]>([]);
    readonly previewFile = signal<PhotoCaptureFile | null>(null);
    readonly canAddMore = computed(
        () => this.photos().length < this.maxPhotos()
    );
    readonly captureLabel = computed(() =>
        this.photos().length > 0 ? 'Take Another Photo' : 'Take Photo'
    );
    readonly uploadLabel = computed(() =>
        this.photos().length > 0 ? 'Upload Another' : 'Upload Photo'
    );

    constructor() {
        effect(() => {
            const initial = this.initialPhotos();
            if (initial.length > 0 && this.photos().length === 0) {
                this.photos.set([...initial]);
            }
        });
    }

    protected onPhotoCaptured(file: PhotoCaptureFile) {
        if (this.photos().length >= this.maxPhotos()) return;
        const updated = [...this.photos(), file];
        this.photos.set(updated);
        this.photosChanged.emit(updated);
    }

    protected onDelete(file: PhotoCaptureFile) {
        if (file.thumbUrl) {
            URL.revokeObjectURL(file.thumbUrl);
        }
        const updated = this.photos().filter(p => p !== file);
        this.photos.set(updated);
        this.photosChanged.emit(updated);
        if (this.previewFile() === file) {
            this.previewFile.set(null);
        }
    }

    protected onPreview(file: PhotoCaptureFile) {
        this.previewFile.set(file);
    }

    protected onClosePreview() {
        this.previewFile.set(null);
    }
}
